// BrewAndBlendLayout.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, 
  AWS_URL, FaHeart, HeroImage
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-amber-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-amber-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function BrewAndBlendLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#78350f";
  const secondary = T.secondaryColor || "#d97706";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedBrewGuide, setSelectedBrewGuide] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [coffeeAnimationComplete, setCoffeeAnimationComplete] = useState(false);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Coffee hero images
  const coffeeHeroImages = [
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cc9b49b9-08ab-448a-9263-a02e1963212d_a.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6d0f8a69-e6a3-41e5-b14f-771826f49a27_b.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/dcba29b6-8516-4676-9a31-1484454b24a3_c.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b35618a5-df5c-4446-94a9-4453e9331a93_d.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2f541068-2acb-4079-a064-9a9a76c52c83_e.jpg",
  ];

  // Beverage collection data
  const beverageCollection = [
    {
      id: 1,
      nameKey: "drink1_name",
      name: "Artisan Cold Brew",
      priceKey: "drink1_price",
      price: 599,
      originalPriceKey: "drink1_original_price",
      originalPrice: 899,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7c8d408e-72c0-44b7-a3f9-9ed373a1a2a9_f.jpg",
      categoryKey: "drink1_category",
      category: "Coffee",
      descriptionKey: "drink1_description",
      description: "Small-batch cold brew, steeped for 24 hours for maximum smoothness.",
      discountKey: "drink1_discount",
      discount: 33,
      badge: "Bestseller",
      badgeColor: "#d97706"
    },
    {
      id: 2,
      nameKey: "drink2_name",
      name: "Chai Spice Latte",
      priceKey: "drink2_price",
      price: 499,
      originalPriceKey: "drink2_original_price",
      originalPrice: 699,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b35618a5-df5c-4446-94a9-4453e9331a93_d.jpg",
      categoryKey: "drink2_category",
      category: "Tea",
      descriptionKey: "drink2_description",
      description: "Authentic Indian chai with aromatic spices and creamy milk.",
      discountKey: "drink2_discount",
      discount: 29,
      badge: "New",
      badgeColor: "#059669"
    },
    {
      id: 3,
      nameKey: "drink3_name",
      name: "Mango Tango Smoothie",
      priceKey: "drink3_price",
      price: 399,
      originalPriceKey: "drink3_original_price",
      originalPrice: 549,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/176c6ce8-0e1d-4289-8638-1225acf9676f_g.jpg",
      categoryKey: "drink3_category",
      category: "Smoothie",
      descriptionKey: "drink3_description",
      description: "Tropical mango blended with coconut milk and passion fruit.",
      discountKey: "drink3_discount",
      discount: 27,
      badge: "Seasonal",
      badgeColor: "#f59e0b"
    },
    {
      id: 4,
      nameKey: "drink4_name",
      name: "Caramel Macchiato",
      priceKey: "drink4_price",
      price: 649,
      originalPriceKey: "drink4_original_price",
      originalPrice: 899,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2f541068-2acb-4079-a064-9a9a76c52c83_e.jpg",
      categoryKey: "drink4_category",
      category: "Coffee",
      descriptionKey: "drink4_description",
      description: "Rich espresso with smooth caramel and steamed milk.",
      discountKey: "drink4_discount",
      discount: 28,
      badge: "Popular",
      badgeColor: "#dc2626"
    },
    {
      id: 5,
      nameKey: "drink5_name",
      name: "Matcha Latte",
      priceKey: "drink5_price",
      price: 549,
      originalPriceKey: "drink5_original_price",
      originalPrice: 749,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/47e96cec-8a93-43e8-9001-998bbdfda082_h.jpg",
      categoryKey: "drink5_category",
      category: "Tea",
      descriptionKey: "drink5_description",
      description: "Ceremonial grade matcha with oat milk and honey.",
      discountKey: "drink5_discount",
      discount: 27,
      badge: "Premium",
      badgeColor: "#7c3aed"
    },
    {
      id: 6,
      nameKey: "drink6_name",
      name: "Strawberry Basil Lemonade",
      priceKey: "drink6_price",
      price: 349,
      originalPriceKey: "drink6_original_price",
      originalPrice: 499,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c8deb697-6721-40a2-a0cc-1260b566a931_i.jpg",
      categoryKey: "drink6_category",
      category: "Juice",
      descriptionKey: "drink6_description",
      description: "Refreshing lemonade with fresh strawberries and basil.",
      discountKey: "drink6_discount",
      discount: 30,
      badge: "Refreshing",
      badgeColor: "#ec4899"
    },
    {
      id: 7,
      nameKey: "drink7_name",
      name: "Bourbon Vanilla Shake",
      priceKey: "drink7_price",
      price: 449,
      originalPriceKey: "drink7_original_price",
      originalPrice: 649,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/617df0fe-090e-44a0-b548-70a26b497029_j.jpg",
      categoryKey: "drink7_category",
      category: "Milkshake",
      descriptionKey: "drink7_description",
      description: "Creamy vanilla shake with a hint of bourbon.",
      discountKey: "drink7_discount",
      discount: 31,
      badge: "Indulgent",
      badgeColor: "#8b5cf6"
    },
    {
      id: 8,
      nameKey: "drink8_name",
      name: "Cucumber Mint Spritzer",
      priceKey: "drink8_price",
      price: 299,
      originalPriceKey: "drink8_original_price",
      originalPrice: 449,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f204326a-3716-4a75-9a12-437377894265_k.jpg",
      categoryKey: "drink8_category",
      category: "Juice",
      descriptionKey: "drink8_description",
      description: "Crisp cucumber and mint with sparkling water and lime.",
      discountKey: "drink8_discount",
      discount: 33,
      badge: "Fresh",
      badgeColor: "#14b8a6"
    }
  ];

  // Brew guides
  const brewGuides = [
    {
      id: 1,
      titleKey: "guide1_title",
      title: "Pour Over Perfection",
      descKey: "guide1_desc",
      desc: "Master the art of pour over coffee with our step-by-step guide.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/46600c43-ac93-435b-ab2e-289d5ba9fa80_l.jpg",
      steps: [
        "Grind 20g of fresh beans to medium-coarse",
        "Heat water to 195-205°F (90-96°C)",
        "Bloom with 40g water, wait 30 seconds",
        "Pour slowly in circular motions",
        "Let it drip and enjoy!"
      ],
      difficulty: "Intermediate",
      time: "5 min"
    },
    {
      id: 2,
      titleKey: "guide2_title",
      title: "Perfect Cold Brew",
      descKey: "guide2_desc",
      desc: "Smooth, low-acid cold brew made simple.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5f617900-76f8-440b-99d2-b54a2975765f_m.jpg",
      steps: [
        "Coarsely grind 100g of beans",
        "Add to 4 cups of cold water",
        "Steep for 16-24 hours",
        "Filter through a fine mesh",
        "Serve over ice with optional milk"
      ],
      difficulty: "Beginner",
      time: "18 hours"
    },
    {
      id: 3,
      titleKey: "guide3_title",
      title: "Tea Brewing 101",
      descKey: "guide3_desc",
      desc: "Unlock the full flavor of loose leaf teas.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f08308ee-e30d-4483-9225-669800e4c1d8_n.webp",
      steps: [
        "Use fresh, filtered water",
        "Heat to proper temperature (175-212°F)",
        "Steep for 2-5 minutes",
        "Strain and enjoy",
        "Re-steep for multiple infusions"
      ],
      difficulty: "Beginner",
      time: "3 min"
    }
  ];

  // Blog posts
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Art of Coffee Roasting",
      descKey: "post1_desc",
      desc: "From bean to cup: our master roaster's journey.",
      fullContentKey: "post1_content",
      fullContent: "Every bean tells a story. Our master roaster has been perfecting the craft for over 20 years, working directly with farmers to source the finest beans from around the world. From the highlands of Ethiopia to the volcanic soils of Colombia, each origin brings its own unique character to the cup.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/6d0f8a69-e6a3-41e5-b14f-771826f49a27_b.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Elena Vasquez",
      category: "Roasting"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Sustainable Sourcing",
      descKey: "post2_desc",
      desc: "Our commitment to ethical coffee production.",
      fullContentKey: "post2_content",
      fullContent: "Sustainability isn't just a buzzword for us—it's a way of life. We work directly with farming cooperatives to ensure fair wages, organic practices, and environmental stewardship. From shade-grown coffee to water conservation, every step of our supply chain is designed with the planet in mind.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cc9b49b9-08ab-448a-9263-a02e1963212d_a.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Marcus Chen",
      category: "Sustainability"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Tea Tasting Notes",
      descKey: "post3_desc",
      desc: "A guide to our artisanal tea collection.",
      fullContentKey: "post3_content",
      fullContent: "Discover the subtle flavors and aromas of our single-origin teas. From delicate white teas to robust black blends, each variety offers a unique sensory experience. Our tea sommeliers have curated a collection that celebrates the diversity of tea culture.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b35618a5-df5c-4446-94a9-4453e9331a93_d.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Priya Sharma",
      category: "Tea"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_coffee", label: "COFFEE", page: "coffee" },
    { key: "nav_tea", label: "TEA", page: "tea" },
    { key: "nav_menu", label: "MENU", page: "menu" },
    { key: "nav_brew_guide", label: "BREW GUIDE", page: "brew-guide" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Drink categories
  const drinkCategories = [
    { key: "cat_coffee", icon: "☕", label: "Coffee", color: "#78350f", bgColor: "#fef3c7" },
    { key: "cat_tea", icon: "🍵", label: "Tea", color: "#059669", bgColor: "#ecfdf5" },
    { key: "cat_smoothie", icon: "🥤", label: "Smoothies", color: "#f59e0b", bgColor: "#fffbeb" },
    { key: "cat_juice", icon: "🍹", label: "Juices", color: "#ec4899", bgColor: "#fdf2f8" },
  ];

  // Top bar messages
  const topBarMessages = [
    "☕ FRESH ROASTED DAILY",
    "✦ FREE DELIVERY ON FIRST ORDER",
    "☕ ETHICALLY SOURCED BEANS",
    "✦ 30-DAY SATISFACTION GUARANTEE"
  ];

  // Particles (steam/aroma effect)
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 8,
    delay: Math.random() * 5,
    duration: 4 + Math.random() * 6,
    opacity: 0.1 + Math.random() * 0.3,
  }));

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % coffeeHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setCoffeeAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 100 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openBrewGuide = useCallback((guide, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedBrewGuide(guide);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBrewGuide = useCallback(() => {
    setSelectedBrewGuide(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedDrink(null);
      setSelectedPost(null);
      setSelectedBrewGuide(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "BREW & BLEND",
    title: p.storeTheme?.heroTitle || "Crafted for Flavor",
    subtitle: p.storeTheme?.heroSubtitle || "Artisanal beverages, ethically sourced and expertly prepared",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Drinks",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Menu",
  };

  const isDarkTheme = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDark = isDarkTheme(T.backgroundColor);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -80, y: -30, rotate: -6 },
      { x: 80, y: -30, rotate: 6 },
      { x: -80, y: 30, rotate: -4 },
      { x: 80, y: 30, rotate: 4 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-amber-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Nunito', sans-serif" }}>
      
      {/* Floating Aroma Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-aroma"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-amber-900 text-amber-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      {/* Navigation - Warm Brown Theme */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-amber-900/95 backdrop-blur-xl shadow-lg py-2" : "bg-amber-900/80 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-amber-400" : "border-amber-300"}`} />
            <span className={`font-bold text-xl tracking-tight transition-colors text-amber-100`}>
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Brew & Blend"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${activePage === item.page ? "text-amber-300" : "text-amber-100/80 hover:text-amber-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-amber-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-amber-800/50">
                <FaHeart className="text-lg text-amber-200 hover:text-amber-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu - Brown Theme */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-amber-900 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-amber-800">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-amber-800/50 text-amber-200" : "text-amber-100/80 hover:text-amber-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full">
              {coffeeHeroImages.map((img, idx) => (
                <div 
                  key={idx} 
                  className={`absolute inset-0 w-full transition-opacity duration-1000 ease-in-out ${idx === activeSlide ? "opacity-100" : "opacity-0"}`}
                >
                  <img 
                    src={img} 
                    alt={`Coffee ${idx + 1}`} 
                    className="w-full h-full object-cover object-center" 
                    style={{ 
                      transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                      transition: "transform 0.3s ease-out" 
                    }} 
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-900/80 via-amber-900/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-amber-50 via-transparent to-transparent" />
            </div>
            
            <div className="relative h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-amber-800/30 backdrop-blur-md rounded-full border border-amber-400/30">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                    <span className="text-amber-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-amber-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("coffee", e)} 
                    className="group px-8 py-4 rounded-full bg-amber-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="coffee"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("menu", e)} 
                    className="px-8 py-4 rounded-full border-2 border-amber-400/50 text-amber-200 font-semibold tracking-wide transition-all duration-300 hover:bg-amber-600/30 hover:border-amber-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="menu"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
              <div className="flex flex-col items-center gap-1">
                <span className="text-amber-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-amber-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-amber-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-amber-100">
              <span className="text-amber-600 text-sm">☕</span>
              <span className="text-amber-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="FRESH ROASTED COLLECTION"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="coffee"
                />
              </span>
              <span className="text-amber-600 text-sm">☕</span>
            </div>
          </div>
          
          {/* Drink Categories */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {drinkCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.12}s` }}
                  onClick={(e) => {
                    const pageMap = {
                      cat_coffee: "coffee",
                      cat_tea: "tea",
                      cat_smoothie: "menu",
                      cat_juice: "menu"
                    };
                    navigateTo(pageMap[cat.key] || "menu", e);
                  }}
                >
                  <div 
                    className="relative overflow-hidden rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                    style={{ backgroundColor: cat.bgColor }}
                  >
                    <div className="text-5xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {cat.icon}
                    </div>
                    <h3 className="font-bold" style={{ color: cat.color }}>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cat.key} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <div className="mt-2 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Drinks */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Signature"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Featured Brews"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {beverageCollection.slice(0, 4).map((drink, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = coffeeAnimationComplete;
                return (
                  <div 
                    key={drink.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedDrink(drink)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                      <div className="relative aspect-square overflow-hidden bg-amber-50">
                        <img src={drink.image} alt={drink.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        {drink.badge && (
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: drink.badgeColor }}>
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${drink.nameKey}_badge`} 
                              value={drink.badge}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                        {drink.discount > 0 && (
                          <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            {drink.discount}% OFF
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={drink.nameKey} 
                            value={drink.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <span className="text-xl font-bold text-amber-700">₹<InlineEditableTextWithNav 
                            p={p} 
                            field={drink.priceKey} 
                            value={drink.price.toString()}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          /></span>
                          {drink.originalPrice > drink.price && (
                            <span className="text-sm text-gray-400 line-through">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={drink.originalPriceKey} 
                              value={drink.originalPrice.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={drink.descriptionKey} 
                            value={drink.description}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => navigateTo("menu", e)} 
                className="px-8 py-3 rounded-full bg-amber-700 text-white font-semibold hover:bg-amber-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View Full Menu"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="menu"
                />
              </button>
            </div>
          </section>
          
          {/* Brew Guide Preview */}
          <section className="w-full px-4 md:px-8 py-12 bg-white">
            <div className="text-center mb-10">
              <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="brew_guide_badge" 
                  value="Learn"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="brew-guide"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="brew_guide_title" 
                  value="Brew Guide"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="brew-guide"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {brewGuides.map((guide) => (
                <div 
                  key={guide.id} 
                  className="group cursor-pointer bg-amber-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                  onClick={(e) => openBrewGuide(guide, e)}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full font-semibold">
                        {guide.difficulty}
                      </span>
                      <span className="text-xs text-gray-500">⏱ {guide.time}</span>
                    </div>
                    <h3 className="text-lg font-bold text-amber-900 mb-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.titleKey} 
                        value={guide.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="brew-guide"
                      />
                    </h3>
                    <p className="text-gray-600 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={guide.descKey} 
                        value={guide.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="brew-guide"
                      />
                    </p>
                    <div className="mt-3 text-amber-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="learn_more_button" 
                        value="Learn More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="brew-guide"
                      />
                      <span>→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== COFFEE PAGE ==================== */}
      {activePage === "coffee" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="coffee_badge" 
                value="Our Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="coffee"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="coffee_title" 
                value="Artisanal Coffees"
                onNavigate={handleNavigateAfterEdit}
                navPage="coffee"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="coffee_subtitle" 
                value="Sourced from the finest farms, roasted to perfection"
                onNavigate={handleNavigateAfterEdit}
                navPage="coffee"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {beverageCollection.filter(d => d.category === "Coffee").map((drink, idx) => (
              <div 
                key={drink.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedDrink(drink)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative aspect-square overflow-hidden bg-amber-50">
                    <img src={drink.image} alt={drink.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    {drink.discount > 0 && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {drink.discount}% OFF
                      </div>
                    )}
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-gray-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={drink.nameKey} 
                        value={drink.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="coffee"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xl font-bold text-amber-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={drink.priceKey} 
                        value={drink.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="coffee"
                      /></span>
                      {drink.originalPrice > drink.price && (
                        <span className="text-sm text-gray-400 line-through">₹<InlineEditableTextWithNav 
                          p={p} 
                          field={drink.originalPriceKey} 
                          value={drink.originalPrice.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="coffee"
                        /></span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== TEA PAGE ==================== */}
      {activePage === "tea" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-emerald-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="tea_badge" 
                value="Tea Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="tea"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-emerald-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="tea_title" 
                value="Artisanal Teas"
                onNavigate={handleNavigateAfterEdit}
                navPage="tea"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="tea_subtitle" 
                value="Single-origin teas with complex flavors and aromas"
                onNavigate={handleNavigateAfterEdit}
                navPage="tea"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-emerald-600 to-emerald-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {beverageCollection.filter(d => d.category === "Tea").map((drink) => (
              <div 
                key={drink.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedDrink(drink)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative aspect-square overflow-hidden bg-emerald-50">
                    <img src={drink.image} alt={drink.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-gray-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={drink.nameKey} 
                        value={drink.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="tea"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xl font-bold text-emerald-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={drink.priceKey} 
                        value={drink.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="tea"
                      /></span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== MENU PAGE ==================== */}
      {activePage === "menu" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="menu_badge" 
                value="Full Menu"
                onNavigate={handleNavigateAfterEdit}
                navPage="menu"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="menu_title" 
                value="Our Specialties"
                onNavigate={handleNavigateAfterEdit}
                navPage="menu"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="menu_subtitle" 
                value="Explore our complete beverage menu"
                onNavigate={handleNavigateAfterEdit}
                navPage="menu"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Menu by Category */}
          {["Coffee", "Tea", "Smoothie", "Juice", "Milkshake"].map((category) => {
            const items = beverageCollection.filter(d => d.category === category);
            if (items.length === 0) return null;
            return (
              <div key={category} className="mb-12">
                <h3 className="text-2xl font-bold text-amber-900 mb-6 border-b-2 border-amber-200 pb-3">
                  {category}s
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {items.map((drink) => (
                    <div 
                      key={drink.id} 
                      className="bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      onClick={() => setSelectedDrink(drink)}
                    >
                      <div className="h-20 w-20 mx-auto rounded-full overflow-hidden border-2 border-amber-200">
                        <img src={drink.image} alt={drink.name} className="w-full h-full object-cover" />
                      </div>
                      <h4 className="font-semibold text-gray-800 text-sm mt-3 text-center">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={drink.nameKey} 
                          value={drink.name}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="menu"
                        />
                      </h4>
                      <p className="text-amber-700 font-bold text-center">
                        ₹<InlineEditableTextWithNav 
                          p={p} 
                          field={drink.priceKey} 
                          value={drink.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="menu"
                        />
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}
      
      {/* ==================== BREW GUIDE PAGE ==================== */}
      {activePage === "brew-guide" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="guide_badge" 
                value="Learn"
                onNavigate={handleNavigateAfterEdit}
                navPage="brew-guide"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="guide_title" 
                value="Brew Guide"
                onNavigate={handleNavigateAfterEdit}
                navPage="brew-guide"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="guide_subtitle" 
                value="Master the art of brewing with our expert guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="brew-guide"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {brewGuides.map((guide) => (
              <div 
                key={guide.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBrewGuide(guide, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={guide.image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">
                      {guide.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">⏱ {guide.time}</span>
                  </div>
                  <h3 className="text-xl font-bold text-amber-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.titleKey} 
                      value={guide.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="brew-guide"
                    />
                  </h3>
                  <p className="text-gray-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={guide.descKey} 
                      value={guide.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="brew-guide"
                    />
                  </p>
                  <div className="mt-4 text-amber-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="read_guide_button" 
                      value="Read Guide"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="brew-guide"
                    />
                    <span>→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Brew Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Stories from the world of coffee and tea"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-amber-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-amber-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-amber-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-amber-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Passion in Every Cup"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-amber-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="At Brew & Blend, we believe that every cup tells a story. From the farmers who nurture the beans to the roasters who bring out their unique character, we're passionate about creating exceptional beverage experiences."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="Our commitment to quality and sustainability drives everything we do. We work directly with growers, use eco-friendly packaging, and constantly innovate to bring you the best possible beverage experience."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="100+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Coffee Varieties"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Tea Blends"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="10k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80" 
                alt="About Brew & Blend" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "☕ Shop Drinks"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-amber-700 text-white shadow-lg" : "text-gray-500 hover:text-amber-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-amber-800 to-amber-900 py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-amber-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Brew Club"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-amber-200 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get 10% off your first order + exclusive brewing tips"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-amber-400/30 rounded-full text-white placeholder-amber-300/50 focus:border-amber-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Drink Modal */}
      {selectedDrink && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-72">
              <img src={selectedDrink.image} alt={selectedDrink.name} className="w-full h-full object-cover" />
              <button 
                onClick={() => setSelectedDrink(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedDrink.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedDrink.badgeColor }}>
                  {selectedDrink.badge}
                </div>
              )}
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-amber-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedDrink.nameKey} 
                  value={selectedDrink.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedDrink.descriptionKey} 
                  value={selectedDrink.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-amber-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedDrink.priceKey} 
                    value={selectedDrink.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedDrink.originalPrice > selectedDrink.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedDrink.originalPriceKey} 
                      value={selectedDrink.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-semibold">
                  {selectedDrink.category}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Brew Guide Modal */}
      {selectedBrewGuide && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <img src={selectedBrewGuide.image} alt={selectedBrewGuide.title} className="w-full h-full object-cover" />
              <button 
                onClick={closeBrewGuide} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              <div className="absolute bottom-4 left-4 flex gap-2">
                <span className="bg-amber-800/80 text-amber-200 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
                  {selectedBrewGuide.difficulty}
                </span>
                <span className="bg-amber-800/80 text-amber-200 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
                  ⏱ {selectedBrewGuide.time}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-amber-900 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedBrewGuide.titleKey} 
                  value={selectedBrewGuide.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="brew-guide"
                  isInModal={true}
                />
              </h2>
              <div className="space-y-4">
                {selectedBrewGuide.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                    <span className="w-8 h-8 rounded-full bg-amber-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeBrewGuide} 
                  className="px-6 py-2 rounded-full bg-amber-700 text-white hover:bg-amber-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="brew-guide"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-amber-900">Brew Stories</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover rounded-xl" />
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-amber-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-amber-700 text-white hover:bg-amber-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-aroma { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.1; } 50% { transform: translateY(-80px) translateX(30px); opacity: 0.5; } }
        .animate-float-aroma { animation: float-aroma 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .delay-100 { animation-delay: 0.1s; animation-fill-mode: forwards; }
        .delay-200 { animation-delay: 0.2s; animation-fill-mode: forwards; }
        .delay-300 { animation-delay: 0.3s; animation-fill-mode: forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}